import { useCallback, useEffect, useRef, useState } from "react";

const MAX_DURATION_SEC = 30;

export type LoadBriefRecording = {
  blob: Blob;
  mime: string;
  durationSec: number;
  url: string;
};

export type RecorderState = "idle" | "requesting" | "recording" | "recorded" | "error";

/**
 * Minimal MediaRecorder wrapper for the optional Load Brief.
 * - 30s hard cap (auto-stops)
 * - returns an opus/webm Blob + object URL for preview
 * - exposes the live MediaStream so a volume meter can attach
 * - cleans up on unmount / reset
 */
export function useLoadBriefRecorder() {
  const [state, setState] = useState<RecorderState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [recording, setRecording] = useState<LoadBriefRecording | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const timerRef = useRef<number | null>(null);
  const stopTimeoutRef = useRef<number | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      setStream(null);
    }
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (stopTimeoutRef.current != null) {
      window.clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    if (recording?.url) URL.revokeObjectURL(recording.url);
    setRecording(null);
    setElapsedSec(0);
    setError(null);
    setState("idle");
    chunksRef.current = [];
    cleanupStream();
  }, [recording, cleanupStream]);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state === "recording") {
      rec.stop();
    }
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setRecording(null);
    chunksRef.current = [];
    setElapsedSec(0);
    setState("requesting");

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = mediaStream;
      setStream(mediaStream);

      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
      ];
      const mimeType = mimeCandidates.find(t =>
        typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)
      ) || "";

      const rec = mimeType
        ? new MediaRecorder(mediaStream, { mimeType })
        : new MediaRecorder(mediaStream);
      recorderRef.current = rec;

      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      rec.onstop = () => {
        const finalMime = rec.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: finalMime });
        const durationSec = Math.min(
          MAX_DURATION_SEC,
          Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000))
        );
        const url = URL.createObjectURL(blob);
        setRecording({ blob, mime: finalMime, durationSec, url });
        setState("recorded");
        cleanupStream();
      };

      rec.onerror = (ev) => {
        console.error("MediaRecorder error", ev);
        setError("Recording failed");
        setState("error");
        cleanupStream();
      };

      startedAtRef.current = Date.now();
      rec.start();
      setState("recording");

      timerRef.current = window.setInterval(() => {
        const sec = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setElapsedSec(sec);
      }, 200);

      // Hard 30s cap
      stopTimeoutRef.current = window.setTimeout(() => {
        if (recorderRef.current && recorderRef.current.state === "recording") {
          recorderRef.current.stop();
        }
      }, MAX_DURATION_SEC * 1000);
    } catch (err: any) {
      console.error("getUserMedia failed", err);
      setError(err?.message || "Microphone unavailable");
      setState("error");
      cleanupStream();
    }
  }, [cleanupStream]);

  // unmount cleanup
  useEffect(() => {
    return () => {
      cleanupStream();
      if (recording?.url) URL.revokeObjectURL(recording.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    error,
    elapsedSec,
    maxSec: MAX_DURATION_SEC,
    recording,
    stream,
    start,
    stop,
    reset,
  };
}
